import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Card, Table, Button, Modal, Form, Input, Select, Space, message, DatePicker, Tag, Switch, Drawer, Timeline } from 'antd';
import dayjs from 'dayjs';
import MainLayout from '../components/MainLayout';
import { userService } from '../services/users';
import { tenantService } from '../services/tenants';
import { useAuth } from '../contexts/AuthContext';
import './Dashboard.css';

const { Title } = Typography;
const { Option } = Select;

function Users() {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [accessLogsOpen, setAccessLogsOpen] = useState(false);
  const [accessLogs, setAccessLogs] = useState([]);
  const [accessLogsUser, setAccessLogsUser] = useState(null);
  const [accessLogsLoading, setAccessLogsLoading] = useState(false);

  const formatError = (err) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return 'Errore';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d) => d.msg || d.detail || JSON.stringify(d)).join(' | ');
    if (typeof detail === 'object') return detail.msg || detail.detail || JSON.stringify(detail);
    return 'Errore';
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const users = await userService.list();
      setData(users);
    } catch (e) {
      message.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const result = await tenantService.list();
      setTenants(result);
    } catch (e) {
      console.error('Errore caricamento tenants:', e);
    }
  };

  useEffect(() => {
    const initLoad = async () => {
      await loadUsers();
      await loadTenants();
    };
    initLoad();
  }, []);

  const filteredData = useMemo(() => {
    if (!search) return data;
    const term = search.toLowerCase();
    return data.filter((item) => Object.values(item || {}).some((val) => (val !== null && val !== undefined) && String(val).toLowerCase().includes(term)));
  }, [data, search]);

  const stringSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '');

  const onSubmit = async (values) => {
    try {
      const payload = {
        email: values.email,
        full_name: values.full_name,
        first_name: values.first_name,
        last_name: values.last_name,
        role: values.role,
        tenant_id: values.tenant_id,
        is_active: values.is_active ?? true,
        hire_date: values.hire_date ? values.hire_date.format('YYYY-MM-DD') : null,
        fiscal_code: values.fiscal_code,
        p_iva: values.p_iva,
        job_role: values.job_role,
        birth_date: values.birth_date ? values.birth_date.format('YYYY-MM-DD') : null,
        birth_place: values.birth_place,
      };

      if (editing) {
        // Update: password è opzionale
        if (values.password) {
          payload.password = values.password;
        }
        await userService.update(editing.id, payload);
        message.success('Utente aggiornato');
      } else {
        // Create: password non viene fornito dal form, il backend genererà una password temporanea
        await userService.create(payload);
        message.success('Utente creato e invito inviato all\'email fornita');
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      loadUsers();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      email: record.email,
      full_name: record.full_name,
      first_name: record.first_name,
      last_name: record.last_name,
      role: record.role,
      tenant_id: record.tenant_id,
      is_active: record.is_active,
      hire_date: record.hire_date ? dayjs(record.hire_date) : null,
      fiscal_code: record.fiscal_code,
      p_iva: record.p_iva,
      job_role: record.job_role,
      birth_date: record.birth_date ? dayjs(record.birth_date) : null,
      birth_place: record.birth_place,
    });
    setModalOpen(true);
  };

  const handleToggleActive = async (record) => {
    try {
      await userService.update(record.id, { is_active: !record.is_active });
      message.success(record.is_active ? 'Utente disattivato' : 'Utente attivato');
      loadUsers();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleResetPassword = (record) => {
    Modal.confirm({
      title: 'Resetta Password',
      content: `Resettare la password per ${record.full_name}? Un\'email con una nuova password temporanea sarà inviata a ${record.email}.`,
      okText: 'Sì, Resetta',
      cancelText: 'Annulla',
      okType: 'danger',
      onOk: async () => {
        try {
          await userService.resetPassword(record.id);
          message.success('Password resettata. Email di invito inviata.');
          loadUsers();
        } catch (e) {
          message.error(formatError(e));
        }
      },
    });
  };

  const handleBulkDisable = async () => {
    try {
      await Promise.all(selectedRowKeys.map(id => userService.update(id, { is_active: false })));
      message.success('Utenti disattivati con successo');
      setSelectedRowKeys([]);
      loadUsers();
    } catch (e) {
      message.error(formatError(e));
    }
  };

  const handleBulkDelete = async () => {
    Modal.confirm({
      title: 'Conferma Eliminazione',
      content: `Sei sicuro di voler eliminare ${selectedRowKeys.length} utenti selezionati? Questa azione non può essere annullata.`,
      okText: 'Elimina',
      okType: 'danger',
      cancelText: 'Annulla',
      onOk: async () => {
        try {
          await userService.bulkDelete(selectedRowKeys);
          message.success('Utenti eliminati con successo');
          setSelectedRowKeys([]);
          loadUsers();
        } catch (e) {
          message.error(formatError(e));
        }
      },
    });
  };

  const handleViewAccessLogs = async (record) => {
    setAccessLogsUser(record);
    setAccessLogsOpen(true);
    setAccessLogsLoading(true);
    try {
      const logs = await userService.getLoginAttempts(record.id);
      setAccessLogs(logs);
    } catch (e) {
      message.error(formatError(e));
    } finally {
      setAccessLogsLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Admin Globale',
      qhse_nke: 'QHSE NKE',
      tenant_user: 'Utente Esterno',
      all_external: 'All External'
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'red',
      qhse_nke: 'blue',
      tenant_user: 'green',
      all_external: 'orange'
    };
    return colors[role] || 'default';
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70, sorter: (a, b) => a.id - b.id, sortDirections: ['ascend', 'descend'] },
    { title: 'Email', dataIndex: 'email', key: 'email', sorter: stringSorter('email'), sortDirections: ['ascend', 'descend'] },
    { title: 'Nome', dataIndex: 'full_name', key: 'full_name', sorter: stringSorter('full_name'), sortDirections: ['ascend', 'descend'] },
    {
      title: 'Ruolo',
      dataIndex: 'role',
      key: 'role',
      sorter: stringSorter('role'),
      sortDirections: ['ascend', 'descend'],
      render: (role) => <Tag color={getRoleColor(role)}>{getRoleLabel(role)}</Tag>
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      sorter: (a, b) => (a.tenant_id || 0) - (b.tenant_id || 0),
      sortDirections: ['ascend', 'descend'],
      render: (tid) => {
        const tenant = tenants.find(t => t.id === tid);
        return tenant ? tenant.name : tid;
      }
    },
    { title: 'Attivo', dataIndex: 'is_active', key: 'is_active', render: (v) => (v ? 'Sì' : 'No') },
    {
      title: 'Azioni',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => handleEdit(record)}>Modifica</Button>
          <Button size="small" onClick={() => handleToggleActive(record)}>
            {record.is_active ? 'Disattiva' : 'Attiva'}
          </Button>
          <Button size="small" danger onClick={() => handleResetPassword(record)}>Resetta Password</Button>
          {(user?.role === 'admin' || user?.role === 'qhse_nke') && (
            <Button size="small" type="link" onClick={() => handleViewAccessLogs(record)}>Log Accessi</Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout title="Utenti">
      <Card style={{ marginBottom: 24 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Title level={2} style={{ margin: 0 }}>Utenti</Title>
          <Input.Search
            allowClear
            placeholder="Cerca in tutti i campi"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <Space>
            {selectedRowKeys.length > 0 && (user?.role === 'admin' || user?.role === 'qhse_nke') && (
              <Button danger type="primary" onClick={handleBulkDelete}>
                Elimina selezionati ({selectedRowKeys.length})
              </Button>
            )}
            <Button
              type="primary"
              size="large"
              onClick={() => {
                setEditing(null);
                form.resetFields();
                setModalOpen(true);
              }}
            >
              + Nuovo Utente
            </Button>
          </Space>
        </Space>
      </Card>

      <Card style={{ marginBottom: 24 }}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          rowSelection={rowSelection}
        />
      </Card>

      <Modal
        title={editing ? 'Modifica Utente' : 'Nuovo Utente'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditing(null); form.resetFields(); }}
        onOk={() => form.submit()}
        okText={editing ? 'Aggiorna' : 'Crea'}
        cancelText="Annulla"
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Inserisci email valida' }]}>
            <Input placeholder="utente@esempio.com" />
          </Form.Item>

          {editing && (
            <Form.Item name="password" label="Nuova Password (opzionale)">
              <Input.Password placeholder="Lascia vuoto per non modificare" />
            </Form.Item>
          )}

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item name="first_name" label="Nome" style={{ flex: 1, marginBottom: 0 }}>
              <Input placeholder="Mario" />
            </Form.Item>
            <Form.Item name="last_name" label="Cognome" style={{ flex: 1, marginBottom: 0 }}>
              <Input placeholder="Rossi" />
            </Form.Item>
          </Space>

          <Form.Item name="full_name" label="Nome Completo" rules={[{ required: true, message: 'Inserisci nome completo' }]}>
            <Input placeholder="Mario Rossi" />
          </Form.Item>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item name="role" label="Ruolo" rules={[{ required: true, message: 'Seleziona ruolo' }]} style={{ flex: 1, marginBottom: 0 }}>
              <Select placeholder="Seleziona ruolo">
                <Option value="admin">Admin Globale</Option>
                <Option value="qhse_nke">QHSE NKE</Option>
                <Option value="tenant_user">Utente Esterno</Option>
                <Option value="all_external">All External</Option>
              </Select>
            </Form.Item>

            <Form.Item name="tenant_id" label="Tenant" rules={[{ required: true, message: 'Seleziona tenant' }]} style={{ flex: 1, marginBottom: 0 }}>
              <Select placeholder="Seleziona tenant" showSearch optionFilterProp="children">
                {tenants.map(t => (
                  <Option key={t.id} value={t.id}>{t.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Space>

          <Form.Item name="job_role" label="Mansione">
            <Input placeholder="Operaio" />
          </Form.Item>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item name="hire_date" label="Data Assunzione" style={{ flex: 1, marginBottom: 0 }}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="birth_date" label="Data Nascita" style={{ flex: 1, marginBottom: 0 }}>
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Form.Item name="birth_place" label="Luogo Nascita">
            <Input placeholder="Milano" />
          </Form.Item>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item name="fiscal_code" label="Codice Fiscale" style={{ flex: 1, marginBottom: 0 }}>
              <Input placeholder="RSSMRA80A01H501U" />
            </Form.Item>
            <Form.Item name="p_iva" label="P. IVA" style={{ flex: 1, marginBottom: 0 }}>
              <Input placeholder="12345678901" />
            </Form.Item>
          </Space>

          <Form.Item name="is_active" label="Attivo" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Sì" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Access Logs Drawer */}
      <Drawer
        title={`Log Accessi - ${accessLogsUser?.full_name || ''}`}
        placement="right"
        width={700}
        open={accessLogsOpen}
        onClose={() => setAccessLogsOpen(false)}
      >
        {accessLogsLoading ? (
          <p>Caricamento...</p>
        ) : accessLogs.length === 0 ? (
          <p>Nessun tentativo di accesso registrato</p>
        ) : (
          <Timeline
            items={accessLogs.map((log) => ({
              color: log.success ? 'green' : 'red',
              children: (
                <div>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>
                    {log.success ? '✓ Accesso riuscito' : '✗ Accesso fallito'}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>
                    {dayjs(log.timestamp).format('DD/MM/YYYY HH:mm:ss')}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px' }}>
                    IP: <strong>{log.ip_address}</strong>
                  </p>
                  {log.failure_reason && (
                    <p style={{ margin: 0, fontSize: '12px', color: 'red' }}>
                      Motivo: {log.failure_reason === 'user_not_found' ? 'Utente non trovato' :
                               log.failure_reason === 'invalid_password' ? 'Password errata' :
                               log.failure_reason === 'user_inactive' ? 'Utente disattivato' :
                               log.failure_reason}
                    </p>
                  )}
                  {log.user_agent && (
                    <p style={{ margin: 0, fontSize: '11px', color: '#aaa' }}>
                      User-Agent: {log.user_agent}
                    </p>
                  )}
                </div>
              ),
            }))}
          />
        )}
      </Drawer>
    </MainLayout>
  );
}

export default Users;
